import React, { useState, useEffect } from 'react';
import { Company, CompanyBankingDetails, CompanyInfoUpdateRequest, UserRole } from '../../types/auth';
import { useAuth } from '../../contexts/AuthContext';

interface CompanyInfoFormProps {
    company: Company | null;
    bankingDetails: CompanyBankingDetails | null;
    onSubmit: (data: CompanyInfoUpdateRequest) => void;
}

export const CompanyInfoForm: React.FC<CompanyInfoFormProps> = ({ company, bankingDetails, onSubmit }) => {
    const { user } = useAuth();
    const [formData, setFormData] = useState<CompanyInfoUpdateRequest>({
        name: '',
        bin: '',
        legalAddress: '',
        factAddress: '',
        contactPhone: '',
        contactEmail: '',
        directorName: '',
        directorIin: '',
        bankName: '',
        kbe: '',
        bik: '',
        accountNumber: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isReadOnly = user?.role === UserRole.EMPLOYEE;

    useEffect(() => {
        console.log('CompanyInfoForm - company data:', company);
        console.log('CompanyInfoForm - banking details:', bankingDetails);

        if (company) {
            setFormData(prev => ({
                ...prev,
                name: company.name || '',
                bin: company.bin || '',
                legalAddress: company.legalAddress || '',
                factAddress: company.factAddress || '',
                contactPhone: company.contactPhone || '',
                contactEmail: company.contactEmail || '',
                directorName: company.directorName || '',
                directorIin: company.directorIin || ''
            }));
        }

        if (bankingDetails) {
            console.log('Setting banking details to form:', {
                bankName: bankingDetails.bankName,
                kbe: bankingDetails.kbe,
                bik: bankingDetails.bik,
                accountNumber: bankingDetails.accountNumber
            });
            setFormData(prev => ({
                ...prev,
                bankName: bankingDetails.bankName || '',
                kbe: bankingDetails.kbe || '',
                bik: bankingDetails.bik || '',
                accountNumber: bankingDetails.accountNumber || '',
            }));
        }
    }, [company, bankingDetails]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            await onSubmit(formData);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="max-w-4xl">
            <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Информация о компании</h2>
                <p className="text-gray-600">
                    {isReadOnly
                        ? 'Просмотр данных компании (только для чтения)'
                        : 'Обновите данные компании и банковские реквизиты'
                    }
                </p>
                {isReadOnly && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm text-blue-700">
                            <strong>Информация:</strong> Только главный аккаунт компании может редактировать данные компании.
                        </p>
                    </div>
                )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
                {/* Основная информация о компании */}
                <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Основная информация</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="md:col-span-2">
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                                Название компании *
                            </label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                                readOnly={true}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                                placeholder="Введите название компании"
                            />
                        </div>

                        <div className="md:col-span-2">
                            <label htmlFor="bin" className="block text-sm font-medium text-gray-700 mb-2">
                                БИН *
                            </label>
                            <input
                                type="text"
                                id="bin"
                                name="bin"
                                value={formData.bin}
                                onChange={handleInputChange}
                                required
                                readOnly={true}
                                maxLength={12}
                                pattern="[0-9]{12}"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                                placeholder="12 цифр БИН"
                            />
                        </div>

                        <div className="md:col-span-2">
                            <label htmlFor="legalAddress" className="block text-sm font-medium text-gray-700 mb-2">
                                Юридический адрес *
                            </label>
                            <textarea
                                id="legalAddress"
                                name="legalAddress"
                                value={formData.legalAddress}
                                onChange={handleInputChange}
                                required
                                rows={3}
                                readOnly={true}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                                placeholder="Введите юридический адрес"
                            />
                        </div>

                        <div className="md:col-span-2">
                            <label htmlFor="factAddress" className="block text-sm font-medium text-gray-700 mb-2">
                                Фактический адрес
                            </label>
                            <textarea
                                id="factAddress"
                                name="factAddress"
                                value={formData.factAddress}
                                onChange={handleInputChange}
                                rows={3}
                                readOnly={true}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                                placeholder="Введите фактический адрес"
                            />
                        </div>

                        <div>
                            <label htmlFor="contactPhone" className="block text-sm font-medium text-gray-700 mb-2">
                                Контактный телефон *
                            </label>
                            <input
                                type="tel"
                                id="contactPhone"
                                name="contactPhone"
                                value={formData.contactPhone}
                                onChange={handleInputChange}
                                required
                                readOnly={isReadOnly}
                                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                placeholder="+7XXXXXXXXXX"
                            />
                        </div>

                        <div>
                            <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700 mb-2">
                                Контактный email *
                            </label>
                            <input
                                type="email"
                                id="contactEmail"
                                name="contactEmail"
                                value={formData.contactEmail}
                                onChange={handleInputChange}
                                required
                                readOnly={isReadOnly}
                                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                placeholder="example@company.com"
                            />
                        </div>

                        <div>
                            <label htmlFor="directorName" className="block text-sm font-medium text-gray-700 mb-2">
                                ФИО директора *
                            </label>
                            <input
                                type="text"
                                id="directorName"
                                name="directorName"
                                value={formData.directorName}
                                onChange={handleInputChange}
                                required
                                readOnly={isReadOnly}
                                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                placeholder="Введите ФИО директора"
                            />
                        </div>

                        <div>
                            <label htmlFor="directorIin" className="block text-sm font-medium text-gray-700 mb-2">
                                ИИН директора
                            </label>
                            <input
                                type="text"
                                id="directorIin"
                                name="directorIin"
                                value={formData.directorIin}
                                onChange={handleInputChange}
                                maxLength={12}
                                pattern="[0-9]{12}"
                                readOnly={isReadOnly}
                                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                placeholder="12 цифр ИИН"
                            />
                        </div>
                    </div>
                </div>

                {/* Банковские реквизиты */}
                <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Банковские реквизиты</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label htmlFor="bankName" className="block text-sm font-medium text-gray-700 mb-2">
                                Название банка
                            </label>
                            <input
                                type="text"
                                id="bankName"
                                name="bankName"
                                value={formData.bankName}
                                onChange={handleInputChange}
                                readOnly={isReadOnly}
                                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                placeholder="Введите название банка"
                            />
                        </div>

                        <div>
                            <label htmlFor="kbe" className="block text-sm font-medium text-gray-700 mb-2">
                                КБЕ
                            </label>
                            <input
                                type="text"
                                id="kbe"
                                name="kbe"
                                value={formData.kbe}
                                onChange={handleInputChange}
                                maxLength={3}
                                pattern="[0-9]{3}"
                                readOnly={isReadOnly}
                                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                placeholder="3 цифры"
                            />
                        </div>

                        <div>
                            <label htmlFor="bik" className="block text-sm font-medium text-gray-700 mb-2">
                                БИК
                            </label>
                            <input
                                type="text"
                                id="bik"
                                name="bik"
                                value={formData.bik}
                                onChange={handleInputChange}
                                maxLength={8}
                                pattern="[0-9]{8}"
                                readOnly={isReadOnly}
                                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                placeholder="8 цифр"
                            />
                        </div>

                        <div>
                            <label htmlFor="accountNumber" className="block text-sm font-medium text-gray-700 mb-2">
                                Номер счета
                            </label>
                            <input
                                type="text"
                                id="accountNumber"
                                name="accountNumber"
                                value={formData.accountNumber}
                                onChange={handleInputChange}
                                readOnly={isReadOnly}
                                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                placeholder="Номер счета"
                            />
                        </div>

                    </div>
                </div>

                {/* Submit Button */}
                {!isReadOnly && (
                    <div className="flex justify-end pt-6 border-t border-gray-200">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {isSubmitting ? 'Сохранение...' : 'Сохранить изменения'}
                        </button>
                    </div>
                )}
            </form>
        </div>
    );
};
